export const useApiFetch = async (
  url: string,
  options?: any,
  anonym: boolean = false
): Promise<any> => {
  options = !options ? {} : options;
  const runtimeConfig = useRuntimeConfig();
  const { $sentryCaptureException } = useNuxtApp();
  const { getAdamAuthHeaders, getAdamHeaders } = useAdamHeader();
  const context = useContextStore();
  const status = ref<number>(null);
  const result = ref<any>(null);
  const error = ref<any>(null);
  const retryCount = ref<number>(0);
  const retryDelayInc = 500;
  const retryDelay = ref<number>(retryDelayInc);
  const retryCountMax = 3;
  const retryStatusCodes = [502];
  let retryTimeout;
  const apiFetch = async (
    url: string,
    options: any,
    anonym: boolean = false
  ): Promise<any> => {
    const adamHeaders = anonym ? getAdamHeaders() : getAdamAuthHeaders();

    return await $fetch(url, {
      retry: 0, // retry via nuxt does not work ...
      baseURL: runtimeConfig.public.apiBase,
      credentials: 'include',
      timeout: 60000,
      parseResponse: (text) => {
        try {
          return JSON.parse(text);
        } catch (e) {
          return text;
        }
      },
      ...options,
      headers: {
        ...adamHeaders,
        ...options.headers,
      },
      async onResponseError(e) {
        // globalThis.$logger.info(
        //   'useApiFetch :: onResponseError',
        //   e,
        //   e.response.status
        // );
        status.value = e.response.status;
        // if (e.response.status === 401) {
        //   globalThis.$logger.info('useApiFetch :: error refresh token');
        // } else if (e.response.status === 502) {
        //   globalThis.$logger.info('useApiFetch :: error 502');
        // }
      },
      async onResponse({ request, response }) {
        status.value = response.status;
        // globalThis.$logger.info(
        //   'useApiFetch :: onResponse',
        //   status.value,
        //   'response',
        //   response
        // );
        if (url === URI.MultiAppEvents) {
          useTrophyRevealFromHeader(response);
          useGamingXPHeader(response);
          useGamingLevelHeader(response);
          useGamingStreakHeader(response);
        }
      },
    });
  };

  const retry = (e) => {
    retryCount.value += 1;
    globalThis.$logger.info(
      'useApiFetch :: retry : count',
      retryCount.value,
      'delay',
      retryDelay.value,
      url
    );
    if (retryCount.value >= retryCountMax) {
      throw e;
    }
    retryTimeout = setTimeout(() => {
      apiAuthFetch(url, options, anonym);
    }, retryDelay.value);
    retryDelay.value += retryDelayInc;
  };

  const apiAuthFetch = async (url: string, options?: any, anonym?: boolean) => {
    globalThis.$logger.info('useApiFetch :: apiAuthFetch', url, options);
    try {
      result.value = await apiFetch(url, options, anonym);
      globalThis.$logger.info('useApiFetch :: completed');
    } catch (e) {
      // only ERROR_REFRESH_TOKEN are send here // TODO manage other adams responses (see onResponseError above)
      globalThis.$logger.warn('useApiFetch', 'error fetching', e);
      if (
        e.response &&
        (e.response.status === 401 || e.response.status === 403) &&
        !anonym
      ) {
        globalThis.$logger.info(
          `useApiFetch :: ${url} error token need to be refreshed`
        );
        if (!toValue(context.adamAuth.isRefreshingToken)) {
          globalThis.$logger.info(`useApiFetch :: ${url} forceRefreshToken`);
          context.adamAuth.forceRefreshToken();
        } else {
          globalThis.$logger.info(
            `useApiFetch :: ${url} waiting for refreshToken`
          );
        }
      } else if (e.response && e.response.status === 502) {
        globalThis.$logger.info(
          'useApiFetch :: error status 502 need to retry'
        );
        retry(e);
      } else if (!e.response) {
        globalThis.$logger.info(
          'useApiFetch :: error response',
          e.response,
          'error',
          e
        );
        if (
          !toValue(context.adamAuth.isRefreshingToken) &&
          url === URI.Profile
        ) {
          globalThis.$logger.info(`useApiFetch :: ${url} forceRefreshToken`);
          context.adamAuth.forceRefreshToken();
        } else {
          error.value = e;
        }
      } else {
        error.value = e;
      }
    }
  };

  const isReady = computed<boolean>(() => {
    return error.value !== null || result.value !== null;
  });

  watch(
    () => status.value,
    (code) => {
      globalThis.$logger.info('useApiFetch :: code', code);
      if (retryStatusCodes.includes(code)) {
        globalThis.$logger.info('need to retry', code);
      }
    }
  );

  const unwatchEffect = watchEffect(() => {
    if (!toValue(context.adamAuth.isRefreshingToken)) {
      globalThis.$logger.info(
        `useApiFetch :: ${url} token is not resfreshing, we call the api`
      );
      apiAuthFetch(url, options, anonym);
    } else {
      globalThis.$logger.info(
        `useApiFetch :: ${url} isRefreshingToken so waiting for token to be refreshed`
      );
    }
  });

  await until(isReady).toBe(true, { timeout: 60000 });

  globalThis.$logger.info(`useApiFetch :: ${url} sending result`);

  clearTimeout(retryTimeout);
  unwatchEffect();

  return {
    result: toRaw(result.value),
    error: toRaw(error.value),
    status: toRaw(status.value),
  };
};
